<script lang="ts" setup>
import { House, LogIn, LogOut, MessageCirclePlus, MessagesSquare, TextAlignEnd, User, X } from 'lucide-vue-next';
import { computed, nextTick, useTemplateRef, watch } from 'vue';
import { RouterLink, useRouter } from 'vue-router';

import { useLandingRoute } from '@/landing/useLandingRoute.js';
import { isBrowser } from '@nzyme/dom-utils/isBrowser.js';
import { waitFor } from '@nzyme/utils/waitFor.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { useService } from '@nzyme/vue-ioc/useService.js';
import { TransitionFade } from '@nzyme/vue-transitions/TransitionFade.jsx';
import { TransitionSlide } from '@nzyme/vue-transitions/TransitionSlide.jsx';
import { AuthStore } from '@sigma/client/auth/AuthStore.js';
import { useLocale } from '@sigma/client/i18n/useLocale.js';
import { ChatsRoute, LogoutRoute, ProfileRoute } from '@sigma/patient-common/routes.js';
import { getLoginRoute } from '@sigma/patient-common/routeUtils.js';
import Avatar from '@sigma/ui/Avatar.vue';
import Button from '@sigma/ui/Button.vue';
import Logo from '@sigma/ui/Logo.vue';
import { getFullName } from '@sigma/utils/getFullName.js';
import { useWindowScroll } from '@vueuse/core';

import * as l from './MainHeader.loc.js';

const menuOpen = defineModel<boolean>('menuOpen', { required: true });

const t = useTranslate();
const locale = useLocale();
const authStore = useService(AuthStore);
const router = useRouter();

// Landing link that preserves utm_campaign so the ad-campaign landing variant survives navigation,
// refresh, and copy/new-tab from the header logo + menu home link.
const landingRoute = useLandingRoute();

const { y: scrollY } = useWindowScroll();

const user = computed(() => authStore.user);
const overlayEl = useTemplateRef<HTMLElement>('overlayEl');
const panelEl = useTemplateRef<HTMLElement>('panelEl');

const isTouchDevice = isBrowser() && (matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0);

watch(menuOpen, async open => {
  if (!open || !isTouchDevice) {
    return;
  }

  await nextTick();
  overlayEl.value?.scrollTo({ top: 0 });
});

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
}

function closeMenu() {
  menuOpen.value = false;
}

function onOverlayScroll(event: Event) {
  if (!isTouchDevice) {
    return;
  }

  const target = event.target as HTMLElement;
  const panelHeight = panelEl.value?.offsetHeight ?? 0;

  if (target.scrollTop > panelHeight * 0.5) {
    closeMenu();
  }
}

async function handleLogout() {
  await router.push(LogoutRoute({ locale: locale.value }));
  await waitFor(1000);
  closeMenu();
}

async function handleLogin() {
  const route = getLoginRoute({
    locale: locale.value,
    redirect: router.currentRoute.value,
  });

  await router.push(route);
  await waitFor(1000);
  closeMenu();
}

async function handleStartNewChat() {
  await router.push(ChatsRoute({ locale: locale.value }));
  await waitFor(1000);
  closeMenu();
}
</script>

<template>
  <header :class="[css.header, (scrollY > 0 || menuOpen) && css.scrolled, menuOpen && css.menuOpen]">
    <nav
      :class="css.inner"
      :aria-label="t(l.mainNavigation)"
    >
      <RouterLink
        tid="header-logo"
        :to="landingRoute"
        :class="css.logo"
        :aria-label="t(l.healedHomepage)"
        @click="closeMenu"
      >
        <Logo :class="css.logoSvg" />
      </RouterLink>

      <button
        :class="css.menuToggle"
        :aria-expanded="menuOpen"
        :aria-label="menuOpen ? t(l.closeMenu) : t(l.openMenu)"
        aria-controls="main-menu-panel"
        @click="toggleMenu"
      >
        <TextAlignEnd :class="[css.toggleIcon, !menuOpen && css.toggleIcon_active]" />
        <X :class="[css.toggleIcon, css.toggleIcon_overlay, menuOpen && css.toggleIcon_active]" />
      </button>
    </nav>

    <div :class="css.menuAnchor">
      <TransitionFade
        :duration="200"
        appear
      >
        <div
          v-if="menuOpen"
          id="main-menu-panel"
          ref="overlayEl"
          :class="[css.overlay, isTouchDevice && css.overlay_touch]"
          @scroll="onOverlayScroll"
          @click.self="closeMenu"
        >
          <TransitionSlide
            direction="down"
            fade
            appear
            :duration="200"
          >
            <div
              v-if="menuOpen"
              ref="panelEl"
              :class="[css.panel, isTouchDevice && css.panel_touch]"
            >
              <div
                v-if="user"
                :class="css.userRow"
              >
                <Avatar
                  :first-name="user.firstName"
                  :last-name="user.lastName"
                  :photo="user.photo"
                  :size="40"
                  color="blue"
                />
                <div :class="css.userInfo">
                  <div :class="css.userName">{{ getFullName(user.firstName, user.lastName) }}</div>
                  <div
                    v-if="user.phone"
                    :class="css.userPhone"
                  >
                    {{ user.phone }}
                  </div>
                </div>
                <button
                  :class="css.logoutBtn"
                  @click="handleLogout"
                >
                  <LogOut :size="14" />
                  {{ t(l.logOut) }}
                </button>
              </div>

              <nav :class="css.navSection">
                <RouterLink
                  :to="landingRoute"
                  :class="css.navItem"
                  @click="closeMenu"
                >
                  <House :size="20" />
                  {{ t(l.mainPage) }}
                </RouterLink>

                <RouterLink
                  v-if="user"
                  :to="ChatsRoute({ locale })"
                  :class="css.navItem"
                  @click="closeMenu"
                >
                  <MessagesSquare :size="20" />
                  {{ t(l.chats) }}
                </RouterLink>

                <RouterLink
                  v-if="user"
                  :to="ProfileRoute({ locale })"
                  :class="css.navItem"
                  @click="closeMenu"
                >
                  <User :size="20" />
                  {{ t(l.yourProfile) }}
                </RouterLink>
              </nav>

              <div :class="css.ctaWrapper">
                <Button
                  v-if="user"
                  variant="primary"
                  color="black"
                  size="large"
                  rounded
                  :icon="MessageCirclePlus"
                  :class="css.ctaBtn"
                  @click="handleStartNewChat"
                >
                  {{ t(l.startNewChat) }}
                </Button>
                <Button
                  v-else
                  variant="primary"
                  color="black"
                  size="large"
                  rounded
                  :icon="LogIn"
                  :class="css.ctaBtn"
                  @click="handleLogin"
                >
                  {{ t(l.logIn) }}
                </Button>
              </div>

              <div :class="[css.handle, !isTouchDevice && css.handleHidden]">
                <div :class="css.handlePill" />
              </div>
            </div>
          </TransitionSlide>
        </div>
      </TransitionFade>
    </div>
  </header>
</template>

<style lang="scss" module="css">
.header {
  background: transparent;
  transition:
    background-color $transition-duration ease-out,
    backdrop-filter $transition-duration ease-out;
  height: $main-header-height;
  display: flex;
  padding-right: var(--body-padding-right);

  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 1px;
    --gradients-border-gradient-direction: 180deg;
    background: linear-gradient(
      var(--gradients-border-gradient-direction),
      $color-patient-200 30%,
      $color-doctor-200 70%
    );
    animation: gradient-rotate 10s $transition-ease-out-quad infinite;
    transition: opacity $transition-duration ease-out;
    opacity: 0;
  }
}

.scrolled {
  background: $opacity-white-80;
  backdrop-filter: blur(20px);

  &:after {
    opacity: 1;
  }
}

.menuOpen {
  background: $color-white;
  backdrop-filter: none;

  &:after {
    opacity: 0;
  }
}

.inner {
  display: flex;
  justify-content: space-between;
  gap: 1em;
  max-width: var(--container-large);
  width: 100%;
  margin: 0 auto;
  z-index: 1;
  background: inherit;
  --padding: clamp(1rem, 2vw, 2rem);
}

.logo {
  padding: var(--padding);
  display: flex;
  margin-right: auto;
  align-items: center;
}

.logoSvg {
  display: block;
  height: 2rem;
  width: auto;
}

// Animated hamburger / X toggle — both icons rendered, crossfade via opacity + rotation

.menuToggle {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  cursor: pointer;
  background: none;
  border: none;
  padding-inline: var(--padding);
  position: relative;
}

.toggleIcon {
  width: 1.5rem;
  height: 1.5rem;
  stroke-width: 1.5;
  transition:
    opacity 0.15s ease-out,
    transform 0.15s ease-out;
  opacity: 0;
  transform: rotate(90deg) scale(0.6);

  &_overlay {
    position: absolute;
  }

  &_active {
    opacity: 1;
    transform: rotate(0) scale(1);
  }
}

// Anchor for the menu — absolutely positioned so it doesn't affect header layout
.menuAnchor {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

// Menu overlay
.overlay {
  height: (100dvh);
  display: flex;
  flex-direction: column;
  background: rgba($color-doctor-50, 0.4);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  overflow-y: auto;

  &_touch {
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

// Menu panel

.panel {
  display: flex;
  flex-direction: column;
  background: $color-white;
  border-radius: 0 0 $border-radius-16 $border-radius-16;
  box-shadow: 0 8px 32px rgba($color-text, 0.1);
  padding-top: $main-header-height;
  flex: 0 0 auto;

  &_touch {
    margin-bottom: 200vh;
  }
}

.userRow {
  display: flex;
  align-items: center;
  gap: $spacing-12;
  padding: $spacing-12 $spacing-20 $spacing-12;
}

.userInfo {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
}

.userName {
  font-size: $font-size-15;
  font-weight: $font-weight-600;
  line-height: 1.2;
}

.userPhone {
  font-size: $font-size-12;
  color: $color-gray-600;
  line-height: 1.2;
}

.logoutBtn {
  display: flex;
  align-items: center;
  gap: $spacing-06;
  padding: $spacing-06 $spacing-12;
  border-radius: $border-radius-08;
  border: 1px solid $color-red-100;
  background: $color-red-50;
  color: $color-red-500;
  font-size: $font-size-12;
  font-weight: $font-weight-600;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s ease;

  @media (hover: hover) {
    &:hover {
      background: $color-red-100;
    }
  }
}

.navSection {
  display: flex;
  flex-direction: column;
  padding: $spacing-08 0;
}

.navItem {
  display: flex;
  align-items: center;
  gap: $spacing-12;
  padding: $spacing-14 $spacing-20;
  font-size: $font-size-15;
  font-weight: $font-weight-500;
  color: $color-text;
  text-decoration: none;
  transition: background-color 0.15s ease;

  svg {
    color: $color-gray-600;
    flex-shrink: 0;
  }

  @media (hover: hover) {
    &:hover {
      background: $color-gray-50;
    }
  }
}

.ctaWrapper {
  padding: $spacing-12 $spacing-20 0;
}

.ctaBtn {
  width: 100%;
}

.handle {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: $spacing-20;
}

.handleHidden {
  visibility: hidden;
}

.handlePill {
  width: 50px;
  height: 4px;
  border-radius: $border-radius-02;
  background: $color-gray-200;
  margin-bottom: $spacing-06;
}
</style>
